import { Divider, Grid, Paper, Typography, Box, TextField, Button, InputAdornment, CircularProgress } from "@material-ui/core";
import LoaderBar from "components/LoaderBar/LoaderBar";
import PageTitle from "components/PageTitle/PageTitle";
import InvertedTable from "components/Table/InvertedTable";
import { useEffect, useState } from "react";
import { dbGet, dbPost } from "utils/DBFetchers";
import DistributorsField from "components/CustomFields/DistributorsField";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import PopupMessage from "components/PopupMessage/PopupMessage";

import useStyles from "./styles";

import PharmacySelector from "components/CustomFields/PharmacySelector";


export default function LaboratoryPharmacyDebitPage() {
  const classes = useStyles();
  const [balance, setBalance] = useState(0);
  const [pharmacyRut, setPharmacyRut] = useState("");
  const [debitValue, setDebitValue] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const MAX_CHARS = 200;
  const restantes = MAX_CHARS - descripcion.length;
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const [showConfirmationPopUp, setShowConfirmationPopUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRut, setSelectedRut] = useState("");
  const isFormEnabled = Boolean(selectedRut);
  const isFormComplete = 
    isFormEnabled && 
    Boolean(debitValue) && 
    Number(debitValue) > 0 && 
    Boolean(descripcion.trim()) &&
    Boolean(selectedRut);

  const RequiredMark = () => <span style={{ color: "#c62828", marginLeft: 4 }}>*</span>;


  const onClickConfirm = () => {
    if (!isFormComplete) return;
    setShowConfirmationPopUp(true);
  };

  const WARN_REMAINING = 20; // cuando queden 20 o menos, “rojo”
  const [shakeCounter, setShakeCounter] = useState(false);

  const counterColor =
    restantes <= 0 ? "#c62828" : restantes <= WARN_REMAINING ? "#c62828" : "#6b6b6b";

  // helper para detectar si la tecla agregaría caracteres
  const willInsertChar = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === "Backspace" || e.key === "Delete" || e.key === "Tab") return false;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight" || e.key === "ArrowUp" || e.key === "ArrowDown") return false;
    return e.key.length === 1 || e.key === "Enter";
  };

  const triggerShake = () => {
    setShakeCounter(true);
    setTimeout(() => setShakeCounter(false), 180); // dura lo mismo que la animación
  };

  const normalizeDebitValueOnBlur = () => {
    if (!debitValue) return;
    let v = debitValue.replace(",", ".");
    if (v === "." || v === "") { setDebitValue("0.00"); return; }
    const n = Number(v);
    if (!isNaN(n)) setDebitValue(n.toFixed(2));
  };

  const onChangeDebitValue = (e) => {
    let value = e.target.value;

    // 1. Quitar caracteres inválidos (solo números, punto o coma)
    value = value.replace(/[^0-9.,]/g, "");

    // 2. Normalizar separador: reemplazar "," por "."
    value = value.replace(",", ".");

    // 3. Asegurar máximo un punto decimal
    if ((value.match(/\./g) || []).length > 1) {
      const firstDot = value.indexOf(".");
      value =
        value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "");
    }

    // 4. Limitar enteros a 15 dígitos
    const parts = value.split(".");
    if (parts[0].length > 15) {
      parts[0] = parts[0].slice(0, 15);
    }

    // 5. Limitar a 2 decimales
    if (parts[1]?.length > 2) {
      parts[1] = parts[1].slice(0, 2);
    }

    value = parts.join(parts.length > 1 ? "." : "");
    setDebitValue(value);
  };

  const onChangeSelectedRut = (rut) => {
    setSelectedRut(rut);
    setDebitValue("");
    setDescripcion("");
  };


  const load_pharmacy_balance = (pharmacy_rut) => {
    if (!pharmacy_rut) {
      setBalance(0);
      return;
    }
    dbGet(`wallet/internal/balances/pharmacies/${pharmacy_rut}`)
      .then((data) => {
        setBalance(data.balance);
      })
      .catch((error) => {
        setBalance(0);
        snackbarOpen("error", "Error de comunicación con el servidor");
      });
  }

  const canUseFull = isFormEnabled && Number(balance) > 0;
  const formattedBalance = canUseFull ? `$${Number(balance).toFixed(2)}` : "saldo";
  const amountColor = canUseFull ? "#2e7d32" : "#9e9e9e"; // mismo verde del saldo


  const handleUseFullBalance = () => {
    const v = Number(balance);
    if (isNaN(v) || v <= 0) return;
    setDebitValue(v.toFixed(2));
  };

  useEffect(() => {
    load_pharmacy_balance(selectedRut);
  }, [selectedRut]);

  const onConfirm = () => {
    setShowConfirmationPopUp(false);
  };

  const onReconfirm = () => {
    if (submitting) return;
    setSubmitting(true);

    const payload = {
      value: Number(debitValue),
      description: descripcion.trim(),
    }
    dbPost(`wallet/internal/laboratory_transactions/pharmacies/${selectedRut}`, payload)
      .then((data) => {
        load_pharmacy_balance(selectedRut)
        setDebitValue("");
        setDescripcion("");
        snackbarOpen("success", "Débito registrado con éxito");
      })
      .catch((error) => {
        const error_key = error?.errors?.key?.[0] || "";
        snackbarOpen("error", error_key || "Error al registrar el débito");
      })
      .finally(() => {
        setSubmitting(false);
        setShowConfirmationPopUp(false);
      });
  };

  return (
    <Grid>
      <PopupMessage
        title={"¿Confirma la operación?"}
        message={"Se registrará un débito por $" + (debitValue ? new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(debitValue) : "0.00")}
        open={showConfirmationPopUp}
        handleClose={onConfirm}
        executeButtonRight={
          <Button
            onClick={onReconfirm}
            variant="contained"
            color="secondary"
            className={classes.buttonSpacing}
            disabled={submitting}
            aria-busy={submitting}
            style={{ minWidth: 140 }} // evita “salto” de layout
          >
            {submitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <CircularProgress size={18} color="inherit" />
                Procesando...
              </span>
            ) : (
              "Confirmar"
            )}
          </Button>
        }
        contentStyle={{ maxWidth: 500 }}
        buttonDivClassName={classes.popupDivCenter}
        buttonClassName={classes.buttonSpacing}
      />
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <PageTitle title="Billetera virtual - Débitos a Farmacias" />
      <Paper elevation={3} className={classes.paperContainer}>
        {/* Contenedor de filas con spacing (v4) */}
        <Grid container direction="column" spacing={3} style={{ padding: 16, background: "#f5f5f5" }}>

          {/* Fila 1: Farmacia + Saldo actual */}
          <Grid item>
            <Grid container alignItems="center" justify="space-between" spacing={2}>

              {/* Columna izquierda: farmacia */}
              <Grid item xs={12} sm>
                <div style={{ maxWidth: 560 }}>
                  <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                    Farmacia con billetera <RequiredMark />
                  </Typography>
                  <PharmacySelector
                    fieldClassName={classes.pharmacySelectionField}
                    value={selectedRut}                  // string con el RUT seleccionado
                    onChange={onChangeSelectedRut}            // te devolverá el rut (string) o "" si se limpia
                    program={1}
                    only_with_wallet={true}
                  />
                </div>
              </Grid>

              {/* Columna derecha: saldo */}
              <Grid item>
                <div style={{ textAlign: "center" }}>
                  <Typography style={{ fontWeight: 700, marginBottom: 8 }}>Saldo actual</Typography>
                  <Paper elevation={2} style={{ borderRadius: 12, background: "#f9f9f9", minWidth: 200, padding: "8px 16px" }}>
                    {(
                      <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
                        {selectedRut ? (
                          <>
                            ${new Intl.NumberFormat("en-US", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            }).format(balance)}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </Typography>
                    )}
                  </Paper>
                </div>
              </Grid>

            </Grid>
          </Grid>

          {/* Fila 2: Monto a debitar */}
          <Grid item>
            <div style={{ maxWidth: 560 }}>
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Monto a debitar <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: isFormEnabled ? "#ffebee" : "#eee",
                  padding: "12px 16px",
                  pointerEvents: isFormEnabled ? "auto" : "none",
                  opacity: isFormEnabled ? 1 : 0.7,
                }}
              >
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder="Ingrese monto"
                  value={debitValue}
                  onChange={onChangeDebitValue}
                  onBlur={normalizeDebitValueOnBlur}
                  disabled={!isFormEnabled}
                  InputProps={{
                    disableUnderline: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: "1.2rem",
                            color: isFormEnabled ? "#c62828" : "#9e9e9e",
                          }}
                        >
                          $
                        </span>
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          size="small"
                          onClick={handleUseFullBalance}
                          disabled={!canUseFull}
                          style={{ fontWeight: 700, textTransform: "none", padding: "2px 8px" }}
                          aria-label="Usar saldo completo"
                        >
                          Usar&nbsp;
                          <span style={{ color: amountColor, fontWeight: 700 }}>
                            {formattedBalance}
                          </span>
                        </Button>
                      </InputAdornment>

                    ),
                    style: {
                      fontSize: "1.2rem",
                      fontWeight: 700,
                      color: isFormEnabled ? "#c62828" : "#9e9e9e",
                    },
                  }}
                />
              </Paper>

            </div>
          </Grid>

          {/* Fila 3: Descripción con contador */}
          <Grid item>
            <div style={{ maxWidth: 560 }}>
              <Typography style={{ fontWeight: 700, marginBottom: 8 }}>
                Descripción <RequiredMark />
              </Typography>
              <Paper
                elevation={2}
                style={{
                  borderRadius: 12,
                  background: "#fff",
                  border: restantes <= WARN_REMAINING && isFormEnabled ? "1px solid #c62828" : "1px solid transparent",
                  pointerEvents: isFormEnabled ? "auto" : "none",
                  opacity: isFormEnabled ? 1 : 0.7,
                }}
              >
                <TextField
                  value={descripcion}
                  onChange={(e) => {
                    if (!isFormEnabled) return;
                    let val = e.target.value;
                    if (val.length > MAX_CHARS) {
                      val = val.slice(0, MAX_CHARS);
                      triggerShake();
                    }
                    setDescripcion(val);
                  }}
                  onKeyDown={(e) => {
                    if (!isFormEnabled) { e.preventDefault(); return; }
                    if (restantes <= 0 && willInsertChar(e)) {
                      e.preventDefault();
                      triggerShake();
                    } else if (restantes <= WARN_REMAINING && willInsertChar(e)) {
                      triggerShake();
                    }
                  }}
                  placeholder="Escriba una nota para el débito a la farmacia…"
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={7}
                  variant="outlined"
                  inputProps={{ maxLength: MAX_CHARS }}
                  InputProps={{ style: { borderRadius: 12 } }}
                  disabled={!isFormEnabled}
                />
                <div
                  className={`${classes.counterFooter} ${shakeCounter ? classes.shake : ""}`}
                  style={{ color: counterColor, opacity: isFormEnabled ? 1 : 0.6 }}
                >
                  <Typography style={{ fontSize: 12 }}>
                    {restantes} caracteres restantes
                  </Typography>
                </div>
              </Paper>

            </div>
          </Grid>


          {/* Fila 4: Botón confirmar (alineado con saldo, derecha) */}
          <Grid item>
            <Grid container justify="flex-end">
              <Grid item>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  style={{ fontWeight: 700, borderRadius: 8, textTransform: "none", padding: "8px 24px" }}
                  onClick={onClickConfirm}
                  disabled={!isFormComplete}
                >
                  Realizar débito
                </Button>

              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </Paper>
    </Grid>

  );
}
